import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer'

Font.register({
  family: 'NotoSansSC',
  fonts: [
    { src: '/fonts/NotoSansSC-Regular.ttf', fontWeight: 'normal' },
    { src: '/fonts/NotoSansSC-Bold.ttf',    fontWeight: 'bold' },
  ],
})

const FONT = 'NotoSansSC'

// ── Layout constants ───────────────────────────────────────────────────────────
// A4 landscape: 842pt × 595pt; padding 25pt each side → usable 792 × 545pt.
const PAD      = 25
const USABLE_H = 595 - PAD * 2   // 545pt

// Minimum data-row height: 60mm × (72pt / 25.4mm) ≈ 170pt.
// Gives 3 P sub-rows of ≈56.7pt ≈ 20mm each — enough for handwriting.
const ROW_H = Math.round((60 * 72) / 25.4)   // 170

// P1-P15 sub-grid: 5 columns × 3 rows, embedded in the pallets cell.
const P_COLS = 5
const P_ROWS = 3

// Column widths in pt — must sum to 792 (= 842 - 2*25 usable A4 landscape).
// #224 v5: N/M widened to 120, Job Ref to 74, Note column added, pallets 170.
// Z50.6: added splitno + pltlocation, shaved other columns to keep summing to 792
// (pallets left untouched — its 170/5=34pt sub-cell width "matches user spec").
const CW = {
  destination: 55,
  split:       48,
  splitno:     35,
  nm:          105,  // wide enough for 3-4 refs per row without overflow
  jobref:      65,   // full KI/KE ref fits on one line
  mode:        38,
  qty:         24,
  wt:          34,
  cbm:         26,
  hold:        20,
  actctns:     32,
  pallets:     170,  // 5 sub-cols × 34pt ≈ 12mm each — matches user spec
  tplts:       26,
  actsum:      26,
  pltlocation: 55,
  check:       13,
  note:        20,   // blank — warehouse handwrites notes during devanning
} as const
type ColId = keyof typeof CW

const P_CELL_W = CW.pallets / P_COLS   // 34pt ≈ 12mm

// Z50.6: the completed "Devan Report" (type="complete") is customer-facing and trims down
// to a clean subset — N/M, Hold, and the P1-P15 sub-grid (all warehouse-internal working
// detail) are dropped, and a new PLT Location column is added. Widths still sum to 792pt.
// Z50.7: 'splitno' (added in Z50.6) removed again per Gary — Split No is a Loading
// List / Devanning detail concept only; the Devan Report shows Group No, not Split No.
const REPORT_CW: Partial<Record<ColId, number>> = {
  destination: 102,
  split:       72,
  jobref:      114,
  mode:        54,
  qty:         31,
  wt:          48,
  cbm:         38,
  tplts:       42,
  actsum:      42,
  pltlocation: 90,
  note:        159,
}
const REPORT_NO_TRUNCATE_COLS: ColId[] = ['destination', 'split', 'jobref']

// Page overhead (pt): page-hdr + tbl-hdr + sep + total + sep + footer.
const HDR_H    = 22
const TBLHDR_H = 18
const SEP_H    = 4
const TOTAL_H  = 18
const FOOTER_H = 42
const OVERHEAD = HDR_H + TBLHDR_H + SEP_H + TOTAL_H + SEP_H + FOOTER_H  // 108

// Page packing is height-aware and computed per-render inside BlankDevanSheetPDF (see packRowsByHeight)

// Z50.6: Simple is the trimmed quick-reference version — N/M and Hold removed
// (it never had Act Ctns/P1-P15 to begin with). Full below keeps every working
// column staff need to physically work the devanning, per Gary's confirmation.
const ROW_H_SIMPLE = 28
const SIMPLE_COL_IDS: ColId[] = ['destination', 'split', 'splitno', 'jobref', 'mode', 'qty', 'wt', 'cbm']
const CW_SIMPLE: Record<string, number> = {
  destination: 165, split: 85, splitno: 55, jobref: 245,
  mode: 68, qty: 50, wt: 64, cbm: 60,
}
const SIMPLE_NO_TRUNCATE_COLS: ColId[] = ['destination', 'split', 'splitno', 'jobref']

// ── Column definitions ─────────────────────────────────────────────────────────
// Z50.6: 'split' is the user-defined Group No ("Group 1"); 'splitno' is the numeric
// Split No suffix (01/02/03) from the Loading List — two distinct concepts, both shown.
const COLS: { id: ColId; label: string }[] = [
  { id: 'destination', label: 'Destination' },
  { id: 'split',       label: 'Group No' },
  { id: 'splitno',     label: 'Split No' },
  { id: 'nm',          label: 'N/M' },
  { id: 'jobref',      label: 'Job Ref' },
  { id: 'mode',        label: 'Del. Mode' },
  { id: 'qty',         label: 'Qty' },
  { id: 'wt',          label: 'WT(KG)' },
  { id: 'cbm',         label: 'CBM' },
  { id: 'hold',        label: 'Hold' },
  { id: 'actctns',     label: 'Act Ctns' },
  { id: 'pallets',     label: 'P1-P15' },
  { id: 'tplts',       label: 'T.PLTS' },
  { id: 'actsum',      label: 'Act Sum' },
  { id: 'pltlocation', label: 'PLT Location' },
  { id: 'check',       label: '✓' },
  { id: 'note',        label: 'Note' },
]

// Z50.6: customer-facing Devan Report column set (type="complete" only) — see REPORT_CW.
const REPORT_COLS: { id: ColId; label: string }[] = [
  { id: 'destination', label: 'Destination' },
  { id: 'split',       label: 'Group No' },
  { id: 'jobref',      label: 'Job Ref' },
  { id: 'mode',        label: 'Del. Mode' },
  { id: 'qty',         label: 'Qty' },
  { id: 'wt',          label: 'WT(KG)' },
  { id: 'cbm',         label: 'CBM' },
  { id: 'tplts',       label: 'T.PLTS' },
  { id: 'actsum',      label: 'Act Sum' },
  { id: 'pltlocation', label: 'PLT Location' },
  { id: 'note',        label: 'Note' },
]

// Columns warehouse fills in — left blank in the PDF.
const BLANK_COLS = new Set<ColId>(['actctns', 'tplts', 'actsum', 'check', 'note', 'pltlocation'])

// ── Types ──────────────────────────────────────────────────────────────────────
export interface BlankSheetRow {
  destination: string
  splitNo:     string
  sysRefs:     string
  jobRefs:     string
  delMode:     string
  qty:         number
  wtKg:        number | null
  cbm:         number | null
  isHold:      boolean
  skipDetail:  boolean   // screen-only flag — PDF always shows P1-P15 sub-grid
  // type="complete" only — actual recorded devanning data (Report.tsx fills these
  // in; full/simple blank sheets leave them undefined and the columns stay blank).
  actCartons?:      number | null
  actPalletDetail?: number[] | null   // P1-P15 actual carton counts
  actTotalPallets?: number | null
  notes?:           string
  pltLocation?:     string | null   // Z50.5 — warehouse location entered post-devanning
  splitNos?:        string   // Z50.3 — numeric Split No suffix(es) (01/02/03), distinct from splitNo (Group No)
}

// ── Helpers ────────────────────────────────────────────────────────────────────
function fmtDate(iso: string | null) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

function fmt(v: number | null | undefined, decimals = 0): string {
  if (v == null || v === 0) return ''
  return decimals > 0 ? v.toFixed(decimals) : String(v)
}

// #183: Destination / Split No / N-M / Job Ref must never be truncated. Since
// react-pdf gives no text-measurement API, estimate wrapped line count from
// column width using a deliberately conservative (narrow) chars-per-line —
// better to size a row a bit tall than risk clipping real content.
const AVG_CHAR_W_RATIO = 0.62
const LINE_H_RATIO     = 1.35
const CELL_PAD_X       = 8   // dataCell paddingHorizontal: 4, both sides
const CELL_PAD_Y       = 6   // dataCell paddingVertical: 3, both sides
const FONT_SIZE        = 9

function estimateLines(text: string, widthPt: number): number {
  if (!text) return 1
  const charsPerLine = Math.max(4, Math.floor((widthPt - CELL_PAD_X) / (FONT_SIZE * AVG_CHAR_W_RATIO)))
  return text.split('\n').reduce((sum, line) => sum + Math.max(1, Math.ceil(line.length / charsPerLine)), 0)
}

// These columns carry free-text that must stay fully visible; their
// wrapped line count drives the row's actual height. Full and Simple use
// different sets since Simple drops N/M entirely (see SIMPLE_NO_TRUNCATE_COLS).
const NO_TRUNCATE_COLS: ColId[] = ['destination', 'split', 'splitno', 'nm', 'jobref']

type Layout = 'full' | 'simple' | 'report'

function computeRowHeight(row: BlankSheetRow, layout: Layout): number {
  const cw: Record<string, number | undefined> = layout === 'report' ? REPORT_CW : layout === 'full' ? CW : CW_SIMPLE
  const baseMin       = layout === 'simple' ? ROW_H_SIMPLE : ROW_H
  const noTruncateCols = layout === 'report' ? REPORT_NO_TRUNCATE_COLS : layout === 'simple' ? SIMPLE_NO_TRUNCATE_COLS : NO_TRUNCATE_COLS
  const values: Record<string, string> = { destination: row.destination, split: row.splitNo, splitno: row.splitNos ?? '', nm: row.sysRefs, jobref: row.jobRefs }
  const maxLines = Math.max(1, ...noTruncateCols.map(id => estimateLines(values[id], cw[id] ?? 0)))
  const contentH = maxLines * FONT_SIZE * LINE_H_RATIO + CELL_PAD_Y
  return Math.max(baseMin, Math.ceil(contentH))
}

interface PackedRow { row: BlankSheetRow; height: number }

// Height-aware pagination: pack rows onto a page until the next row would
// overflow the usable height, rather than assuming every row is the same
// fixed height (which is what caused Simple-mode rows to clip in the first place).
function packRowsByHeight(rows: BlankSheetRow[], layout: Layout, maxHeight: number): PackedRow[][] {
  const items: PackedRow[] = rows.map(row => ({ row, height: computeRowHeight(row, layout) }))
  if (items.length === 0) return [[]]
  const pages: PackedRow[][] = []
  let cur: PackedRow[] = []
  let curH = 0
  for (const item of items) {
    if (cur.length > 0 && curH + item.height > maxHeight) {
      pages.push(cur)
      cur = []
      curH = 0
    }
    cur.push(item)
    curH += item.height
  }
  pages.push(cur)
  return pages
}

// ── Styles ─────────────────────────────────────────────────────────────────────
const BD = { borderWidth: 0.5, borderColor: '#cbd5e1' } as const

const s = StyleSheet.create({
  page: { padding: PAD, fontFamily: FONT, fontSize: 9, color: '#111827', backgroundColor: '#fff' },

  // Page header
  pageHdr:  { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 4, paddingBottom: 3, borderBottomWidth: 1.5, borderColor: '#1e293b' },
  hdrLeft:  { fontSize: 11, fontWeight: 'bold', color: '#1e293b' },
  hdrMid:   { fontSize: 9,  color: '#475569' },
  hdrRight: { fontSize: 9,  color: '#64748b' },

  // Table column header row — fixed height, overflow hidden so long labels never
  // push the row taller or bleed into data rows.
  tblHdrRow:  { flexDirection: 'row', height: TBLHDR_H },
  tblHdrCell: {
    ...BD,
    paddingHorizontal: 3, paddingVertical: 2,
    backgroundColor: '#1e293b',
    justifyContent: 'center', alignItems: 'center',
    overflow: 'hidden',
  },
  // 8pt keeps all labels on one line within their columns
  tblHdrTxt:  { fontSize: 8, fontWeight: 'bold', color: '#f1f5f9', textAlign: 'center' },

  // Data rows — row height is computed per-row (see computeRowHeight) so wrapped
  // text (destination/group/marks/job ref) is never clipped. wordBreak:break-word
  // on the text style keeps long tokens from bleeding into adjacent columns
  // without needing overflow:hidden, which would silently cut off real content.
  dataRow:  { flexDirection: 'row' },
  dataCell: {
    ...BD,
    paddingHorizontal: 4, paddingVertical: 3,
  },
  dataTxt:  { fontSize: 9, color: '#1e293b', wordBreak: 'break-word' },
  holdTxt:  { fontSize: 9, fontWeight: 'bold', color: '#b45309', wordBreak: 'break-word' },

  // P1-P15 embedded 5×3 sub-grid (always rendered — screen skipDetail ignored)
  palletsCell:   { ...BD, width: CW.pallets },          // height from flex stretch
  palletRow:     { flexDirection: 'row', flex: 1 },     // 3 equal-height sub-rows
  palletSubCell: { width: P_CELL_W, paddingTop: 2, paddingLeft: 2 },
  palletLbl:     { fontSize: 7, color: '#9ca3af' },
  palletVal:     { fontSize: 8, fontWeight: 'bold', color: '#1e293b' },

  // Separator
  sep: { borderBottomWidth: 1.5, borderColor: '#475569', marginVertical: 2 },

  // TOTAL row
  totalRow:     { flexDirection: 'row', height: TOTAL_H },
  totalLblCell: { ...BD, paddingHorizontal: 3, paddingVertical: 3, backgroundColor: '#f1f5f9', justifyContent: 'center' },
  totalLblTxt:  { fontSize: 9, fontWeight: 'bold', color: '#334155', textAlign: 'center' },
  totalCell:    { ...BD, paddingHorizontal: 3, paddingVertical: 3, justifyContent: 'center', alignItems: 'center' },
  totalTxt:     { fontSize: 9, color: '#374151' },

  // Footer
  footer:      { flexDirection: 'row', marginTop: 6, paddingTop: 4, borderTopWidth: 1, borderColor: '#94a3b8' },
  footBox:     { flex: 1, borderWidth: 0.5, borderColor: '#94a3b8', padding: 4, marginRight: 4 },
  footBoxLast: { flex: 2, borderWidth: 0.5, borderColor: '#94a3b8', padding: 4 },
  footLbl:     { fontSize: 8, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 14 },
  footLine:    { borderBottomWidth: 0.5, borderColor: '#94a3b8' },
})

// ── PDF component ──────────────────────────────────────────────────────────────
export function BlankDevanSheetPDF({
  jobRef, containerNo, warehouseCode, plannedDevanAt, rows, type = 'full', devannedBy, devannedAt,
}: {
  jobRef:         string
  containerNo:    string
  warehouseCode:  string
  plannedDevanAt: string | null
  rows:           BlankSheetRow[]
  type?:          'full' | 'simple' | 'complete'
  devannedBy?:    string | null   // 'complete' only
  devannedAt?:    string | null   // 'complete' only
}) {
  // Z50.6: 'complete' (the customer-facing Devan Report) gets its own trimmed column set
  // (REPORT_COLS/REPORT_CW) — N/M, Hold, and P1-P15 dropped, PLT Location added. 'full'
  // (pre-devanning blank sheet, warehouse-internal) keeps the original full column set;
  // 'simple' keeps its own reduced set.
  const isComplete = type === 'complete'
  const useFullLayout = type !== 'simple'
  const layout: Layout = isComplete ? 'report' : useFullLayout ? 'full' : 'simple'
  const activeCols = isComplete ? REPORT_COLS : useFullLayout ? COLS : COLS.filter(c => SIMPLE_COL_IDS.includes(c.id as ColId))
  const getW = (id: string) => isComplete ? (REPORT_CW[id as ColId] ?? 0) : useFullLayout ? CW[id as keyof typeof CW] : (CW_SIMPLE[id] ?? 0)
  const pageGroups = packRowsByHeight(rows, layout, USABLE_H - OVERHEAD)
  const totalPages = pageGroups.length

  // Grand totals shown on every page's TOTAL row
  const totalQty = rows.reduce((s, r) => s + r.qty, 0)
  const totalWt  = rows.reduce((s, r) => s + (r.wtKg ?? 0), 0) || null
  const totalCbm = rows.reduce((s, r) => s + (r.cbm   ?? 0), 0) || null

  const titleSuffix = isComplete ? ' — DEVAN REPORT' : useFullLayout ? ' — DEVAN SHEET (FULL)' : ' — DEVAN SHEET (SIMPLE)'
  const titleLeft  = [jobRef || null, containerNo].filter(Boolean).join(' · ') + titleSuffix
  const titleMid   = warehouseCode || ''
  // #285: on the completed report, vessel ETA reads as (and gets confused for)
  // the devan date — show the same devan date as the footer instead. Blank
  // sheets (pre-devanning) keep ETA since there's no devan date yet.
  const eta        = fmtDate(plannedDevanAt)
  const devanDate  = fmtDate(devannedAt ?? null)
  const titleRight = isComplete
    ? (devanDate ? `Devan Date: ${devanDate}` : '')
    : (eta ? `ETA: ${eta}` : '')

  return (
    <Document>
      {pageGroups.map((pageItems, pageIdx) => (
        <Page key={pageIdx} size="A4" orientation="landscape" style={s.page}>

          {/* ── Page header ── */}
          <View style={s.pageHdr}>
            <Text style={s.hdrLeft}>{titleLeft}</Text>
            <Text style={s.hdrMid}>{titleMid}</Text>
            <Text style={s.hdrRight}>
              {titleRight ? `${titleRight}    ` : ''}Page {pageIdx + 1} of {totalPages}
            </Text>
          </View>

          {/* ── Table column headers ── */}
          <View style={s.tblHdrRow}>
            {activeCols.map(col => (
              <View key={col.id} style={[s.tblHdrCell, { width: getW(col.id) }]}>
                <Text style={s.tblHdrTxt}>{col.label}</Text>
              </View>
            ))}
          </View>

          {/* ── Data rows (one per split destination) ── */}
          {pageItems.map(({ row, height: rowH }, ri) => (
            // minHeight (not height): rowH is only an *estimate* used to pack rows
            // onto pages. If real wrapped content needs more room than estimated,
            // the row must grow to fit it — a fixed height here is what caused
            // react-pdf to silently ellipsis-clip text that didn't fit the box.
            <View key={ri} style={[s.dataRow, { minHeight: rowH }]}>
              {activeCols.map(col => {
                // P1-P15 embedded 5×3 sub-grid — full/complete layout only.
                if (col.id === 'pallets' && useFullLayout) {
                  return (
                    <View key="pallets" style={s.palletsCell}>
                      {Array.from({ length: P_ROWS }, (_, pRow) => (
                        <View key={pRow} style={[
                          s.palletRow,
                          pRow < P_ROWS - 1 ? { borderBottomWidth: 0.5, borderColor: '#cbd5e1' } : {},
                        ]}>
                          {Array.from({ length: P_COLS }, (_, pCol) => {
                            const idx = pRow * P_COLS + pCol
                            const actVal = row.actPalletDetail?.[idx]
                            return (
                              <View key={pCol} style={[
                                s.palletSubCell,
                                pCol < P_COLS - 1 ? { borderRightWidth: 0.5, borderColor: '#cbd5e1' } : {},
                              ]}>
                                <Text style={s.palletLbl}>P{idx + 1}</Text>
                                {isComplete && actVal ? <Text style={s.palletVal}>{actVal}</Text> : null}
                              </View>
                            )
                          })}
                        </View>
                      ))}
                    </View>
                  )
                }

                // Warehouse fill-in columns — blank on printed sheets, populated
                // with actual recorded data on the completed Devan Report.
                if (BLANK_COLS.has(col.id)) {
                  if (!isComplete) return <View key={col.id} style={[s.dataCell, { width: getW(col.id) }]} />
                  let fillTxt = ''
                  if      (col.id === 'actctns') fillTxt = fmt(row.actCartons)
                  else if (col.id === 'tplts')   fillTxt = fmt(row.actTotalPallets)
                  else if (col.id === 'actsum')  fillTxt = fmt(row.actCartons)
                  else if (col.id === 'check')   fillTxt = row.actCartons ? (row.actCartons === row.qty ? '✓' : '⚠') : ''
                  else if (col.id === 'note')    fillTxt = row.notes || ''
                  else if (col.id === 'pltlocation') fillTxt = row.pltLocation || ''
                  return (
                    <View key={col.id} style={[s.dataCell, { width: getW(col.id) }]}>
                      <Text style={s.dataTxt}>{fillTxt}</Text>
                    </View>
                  )
                }

                // Pre-filled from DB
                let txt = ''
                if      (col.id === 'destination') txt = row.destination
                else if (col.id === 'split')       txt = row.splitNo
                else if (col.id === 'splitno')     txt = row.splitNos || ''
                else if (col.id === 'nm')          txt = row.sysRefs
                else if (col.id === 'jobref')      txt = row.jobRefs
                else if (col.id === 'mode')        txt = row.delMode
                else if (col.id === 'qty')         txt = fmt(row.qty)
                else if (col.id === 'wt')          txt = fmt(row.wtKg, 1)
                else if (col.id === 'cbm')         txt = fmt(row.cbm, 2)
                else if (col.id === 'hold')        txt = row.isHold ? '⚠ HOLD' : ''

                return (
                  <View key={col.id} style={[s.dataCell, { width: getW(col.id) }]}>
                    <Text style={col.id === 'hold' && row.isHold ? s.holdTxt : s.dataTxt}>
                      {txt}
                    </Text>
                  </View>
                )
              })}
            </View>
          ))}

          {/* ── Separator ── */}
          <View style={s.sep} />

          {/* ── TOTAL row — aggregate totals for all rows, no P sub-grid ── */}
          <View style={s.totalRow}>
            {activeCols.map(col => {
              if (col.id === 'destination') {
                return (
                  <View key="destination" style={[s.totalLblCell, { width: getW('destination') }]}>
                    <Text style={s.totalLblTxt}>TOTAL</Text>
                  </View>
                )
              }
              if (col.id === 'pallets') {
                return <View key="pallets" style={[s.totalCell, { width: getW('pallets') }]} />
              }
              let txt = ''
              if (col.id === 'qty') txt = fmt(totalQty)
              if (col.id === 'wt')  txt = fmt(totalWt,  1)
              if (col.id === 'cbm') txt = fmt(totalCbm, 2)
              return (
                <View key={col.id} style={[s.totalCell, { width: getW(col.id) }]}>
                  {txt ? <Text style={s.totalTxt}>{txt}</Text> : null}
                </View>
              )
            })}
          </View>

          {/* ── Footer ── */}
          {/* #285: signature block is a document footer, not a per-page one —
              only render it on the last page so it doesn't repeat on every page. */}
          {isComplete ? (
            pageIdx === totalPages - 1 && (
              <View style={s.footer}>
                <View style={s.footBox}><Text style={s.footLbl}>Devanned by</Text><Text style={s.dataTxt}>{devannedBy ? [devannedBy, warehouseCode].filter(Boolean).join(' — ') : '—'}</Text></View>
                <View style={s.footBox}><Text style={s.footLbl}>Date</Text><Text style={s.dataTxt}>{fmtDate(devannedAt ?? null) || '—'}</Text></View>
                <View style={s.footBoxLast}><Text style={s.footLbl}>Signature</Text><View style={s.footLine} /></View>
              </View>
            )
          ) : useFullLayout ? (
            <View style={s.footer}>
              <View style={s.footBox}><Text style={s.footLbl}>Devanned by</Text><View style={s.footLine} /></View>
              <View style={s.footBox}><Text style={s.footLbl}>Date</Text><View style={s.footLine} /></View>
              <View style={s.footBoxLast}><Text style={s.footLbl}>Signature</Text><View style={s.footLine} /></View>
            </View>
          ) : (
            <View style={{ marginTop: 6, borderTopWidth: 0.5, borderColor: '#94a3b8', paddingTop: 3 }}>
              <Text style={{ fontSize: 7, color: '#94a3b8' }}>For information only — {containerNo}</Text>
            </View>
          )}

        </Page>
      ))}
    </Document>
  )
}
